import { useEffect } from 'react'

import { useMe, useThreads } from './queries'

/**
 * Die Zahl am App-Symbol — **so viele Beiträge warten** (Badging API).
 *
 * Seit iOS/iPadOS 16.4 darf eine Web-App vom Home-Bildschirm ihr Symbol
 * beschriften. Zwei Bedingungen gelten dafür und sind keine Erfindung dieser
 * Anwendung: sie muss **abgelegt** sein (im Safari-Reiter tut sich nichts),
 * und es muss **Benachrichtigungen erlaubt** sein.
 *
 * **Hier steht nur die Zahl von jetzt.** Kommt etwas an, während die App zu
 * ist, setzt sie der Service Worker aus der Zahl, die im Push mitkommt
 * (`sw.js`); beide holen sie vom Server, gerechnet durch `Visibility`. Ein
 * Zähler im Gerät zählte Push-Ereignisse statt ungelesener Nachrichten — wer
 * auf dem iPad liest, hätte am iPhone weiter die alte Zahl.
 *
 * **Gezählt wird, was die Übersicht zeigt**, und nichts daneben: dieselbe
 * Liste, dieselben Zahlen. Eine Zahl am Symbol, die von der in der Liste
 * abweicht, wäre schlimmer als keine.
 *
 * **Und nichts davon ist fatal.** Wo es die Schnittstelle nicht gibt (jeder
 * Browser außer den paar, die sie haben) oder wo sie ablehnt, passiert
 * schlicht nichts.
 *
 * **Der Schalter am Konto** (0.26.0, `app_badge`): steht er aus, wird die
 * Zahl **weggeräumt** und nicht bloß nicht gesetzt — eine Zahl, die vom
 * Umschalten an stehen bliebe und nie mehr stimmt, wäre schlimmer als keine.
 * Der Push tut dasselbe: der Server schickt dann `0`, und `sw.js` räumt.
 *
 * **Eine stumme Reise zählt nicht mit** (0.26.1). In der Liste steht ihre
 * Zahl weiter, grau; am Symbol nicht — „nicht stören" gilt auch für die
 * Zahl auf dem Home-Bildschirm. Dieselbe Regel steht am Server in
 * `Ungelesen::summe()`, für den Push; hier steht sie ein zweites Mal, weil
 * die Übersicht eine Liste ist und die Summe nicht mitbringen kann.
 * `tests/UngelesenTest.php` und `symbolzahl.mjs` halten beide zusammen.
 */
export function useSymbolzahl(): void {
  const threads = useThreads()
  const me = useMe()
  const daten = threads.data
  // Solange das Konto nicht geladen ist, wird nichts entschieden: sonst
  // stünde die Zahl kurz da und verschwände wieder, oder umgekehrt.
  const an = me.data === undefined ? undefined : me.data.app_badge !== false

  useEffect(() => {
    if (daten === undefined || an === undefined) return

    const summe = an
      ? daten.reduce(
        (zahl, eintrag) => zahl + (eintrag.muted ? 0 : (eintrag.unread ?? 0)),
        0,
      )
      : 0

    void (async () => {
      try {
        if (summe > 0) await navigator.setAppBadge?.(summe)
        else await navigator.clearAppBadge?.()
      } catch {
        // Kein Badge ist kein Fehler der Anwendung.
      }
    })()
  }, [daten, an])
}
